/**
 * Hero / booking service typeahead matching.
 *
 * Two behaviours the naive `label.includes(query)` filter got wrong:
 *
 *  1. Typing a CATEGORY should surface everything it offers. "Plu" used to
 *     return the single row "Plumbing" — the client still had no idea which
 *     plumbing services exist. A category hit now expands to the category plus
 *     all of its subcategories.
 *  2. Mobile autocorrect mangles half-typed service names ("Tutori" →
 *     "Tutorial"), which matched nothing at all. A shared word-prefix is
 *     treated as a hit so near-misses still resolve.
 *
 * Everything returned comes from the admin-active catalogue passed in, so a
 * service that is not on offer can never appear.
 */

export interface ServiceOption {
  label: string;
  /** Parent category — what the booking is filed under. */
  category: string;
}

export interface SearchableCategory {
  name: string;
  subcategories: { name: string }[];
}

/** Lowercase, and reduce punctuation to spaces ("Post-Construction" → "post construction"). */
const normalise = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const commonPrefixLength = (a: string, b: string): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i++;
  return i;
};

/**
 * Shared-prefix length that counts as the same word. 4 is long enough that
 * "care"/"carpentry" (3) stay distinct, short enough that autocorrect slips
 * like "tutorial"/"tutoring" (6) still match.
 */
const FUZZY_PREFIX_MIN = 4;

export function matchesQuery(name: string, query: string): boolean {
  const haystack = normalise(name);
  const needle = normalise(query);
  if (!needle) return true;
  if (haystack.includes(needle)) return true;

  const words = haystack.split(' ');
  // Whole-word prefix: "plumb" → "plumbing".
  if (words.some((word) => word.startsWith(needle))) return true;
  // Typo/autocorrect tolerance on a per-word basis.
  return words.some((word) => commonPrefixLength(word, needle) >= FUZZY_PREFIX_MIN);
}

/**
 * Build the typeahead list.
 *
 * With an empty query this is the major categories only (the agreed default);
 * with a query it is matching subcategories, and every subcategory of any
 * matching category.
 */
export function searchServices(
  categories: SearchableCategory[],
  query: string,
  limit = 24,
): ServiceOption[] {
  const trimmed = query.trim();

  if (!trimmed) {
    return categories.map((cat) => ({ label: cat.name, category: cat.name }));
  }

  const results: ServiceOption[] = [];
  for (const cat of categories) {
    if (matchesQuery(cat.name, trimmed)) {
      // Category hit — offer the category itself and everything under it.
      results.push({ label: cat.name, category: cat.name });
      for (const sub of cat.subcategories) {
        results.push({ label: sub.name, category: cat.name });
      }
      continue;
    }
    for (const sub of cat.subcategories) {
      if (matchesQuery(sub.name, trimmed)) {
        results.push({ label: sub.name, category: cat.name });
      }
    }
  }

  return results.slice(0, limit);
}
